import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import React from "react";
import {
  ConversationUnreadCounts,
  nextTraversalStop,
  previousTraversalStop,
  proposalTraversal,
  ProposalTraversalStop,
  traversalPositionLabel,
} from "work/proposals/issues/proposal-traversal-stops";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * What one stop of the walk is, in the page's own word. A negotiation walks the counterparty's changes;
 * a draft has no counterparty, so it walks the issues it cannot be submitted with.
 */
export type IssueTraverserStopName = "change" | "issue";

/**
 * Walks what is still waiting on the reader: the fields carrying an issue or an unread counterparty
 * change, in page order, then each conversation with unread comments as one stop — All Stakeholders,
 * then Reviewers, as the pane's tabs run — then back to the first field.
 *
 * A conversation is one stop rather than one per comment. A comment is not about any field in
 * particular and cannot say which one to stop at; the stop is the Comments pane on that conversation's
 * tab, scrolled to its oldest unread comment. See `proposal-traversal-stops`.
 *
 * The position between the buttons ("3 of 7") is what makes the wrap visible: the walk already wrapped
 * in both directions, and gave no sign that it had.
 *
 * One outlined group, `[^ Prev] 3 of 7 changes [Next v]`, rather than two outlined buttons either side
 * of the position (#5566). The header seats it beside Save and the page's other actions, and
 * `Prev Change` / `Next Change` buttons that could not shrink took far more of it. The noun moved once,
 * to after the position, where it says what is being walked for both buttons at once. Each button keeps
 * a word as well as its arrow: an arrow alone was too small a thing to hit.
 *
 * With nothing left to walk the control keeps its shape and disables both buttons, reading
 * "– of 0 changes". It once dropped to bare "No changes" text (#5566), and that read as a leftover label
 * rather than the traverser, which had seemed to vanish from the header (#5765).
 *
 * The noun is still part of each button's accessible name ("Prev change"), hidden from sight rather
 * than dropped, and the name starts with the visible word so a voice command for "Prev" still finds it.
 */
type IssueTraverserProps = {
  issueFields: ProposalField[];
  /** Fields whose only pending item is an unread counterparty change (no issue). */
  unreadChangeFields?: ProposalField[];
  currentField: ProposalField | undefined;
  /** The rows of the page's tables in the order each draws them, so a table's stops run down it. */
  rowOrder?: readonly ProposalField[];
  /** Where the traverser last landed, so the stop the reader is on keeps its place once it is read. */
  lastStop?: ProposalTraversalStop;
  /** Each conversation's unread count. A conversation with any adds a stop of its own. */
  unreadCommentCounts?: ConversationUnreadCounts;
  /** Names the buttons and the count. Defaults to "change". */
  stopName?: IssueTraverserStopName;
  disabled?: boolean;
  onTraverseToNewField: (newField: ProposalField) => void;
  /**
   * Arrives at one conversation's unread-comments stop. Without it those stops are left out, since
   * nothing could answer them.
   */
  onTraverseToUnreadComments?: (isExternal: boolean) => void;
};

export default function IssueTraverser(props: Readonly<IssueTraverserProps>) {
  const {
    issueFields,
    unreadChangeFields,
    currentField,
    rowOrder,
    lastStop,
    unreadCommentCounts,
    stopName = "change",
    disabled,
    onTraverseToNewField,
    onTraverseToUnreadComments,
  } = props;

  const traversal = proposalTraversal({
    pendingFields: [...issueFields, ...(unreadChangeFields ?? [])],
    unreadCommentCounts: onTraverseToUnreadComments ? unreadCommentCounts : undefined,
    currentField,
    lastStop,
    rowOrder,
  });
  const { stops, currentIndex } = traversal;

  const traverseTo = (stop: ProposalTraversalStop | undefined) => {
    if (stop?.kind === "field") onTraverseToNewField(stop.field);
    if (stop?.kind === "unreadComments") onTraverseToUnreadComments?.(stop.isExternal);
  };

  const hasNoStop = stops.length === 0;
  const isOnTheOnlyStop = stops.length === 1 && currentIndex === 0;
  const cannotMove = disabled || hasNoStop || isOnTheOnlyStop;

  const previousDescription = `Previous ${stopName}`;
  const nextDescription = `Next ${stopName}`;
  const countedName = stops.length === 1 ? stopName : `${stopName}s`;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        flexShrink: 0,
      }}
      onMouseDown={(event) => event.preventDefault()}
    >
      {/* `describeChild` makes the tooltip a description; without it the Tooltip writes its title over the name. */}
      <Tooltip title={previousDescription} describeChild>
        {/* A disabled button fires no hover events, so the span is what the tooltip listens to. */}
        <span>
          <Button
            id="previous-issue"
            disabled={cannotMove}
            size="small"
            color="primary"
            startIcon={<KeyboardArrowUpIcon />}
            sx={{ minWidth: 0, whiteSpace: "nowrap" }}
            onClick={() => traverseTo(previousTraversalStop(traversal))}
          >
            Prev
            <span style={visuallyHidden}> {stopName}</span>
          </Button>
        </span>
      </Tooltip>
      <Typography
        component="span"
        variant="body2"
        color={disabled ? "text.disabled" : "text.secondary"}
        sx={{ px: 0.5, whiteSpace: "nowrap" }}
      >
        <span data-testid="issue-traverser-position">{traversalPositionLabel(traversal)}</span> {countedName}
      </Typography>
      <Tooltip title={nextDescription} describeChild>
        <span>
          <Button
            id="next-issue"
            disabled={cannotMove}
            size="small"
            color="primary"
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ minWidth: 0, whiteSpace: "nowrap" }}
            onClick={() => traverseTo(nextTraversalStop(traversal))}
          >
            Next
            <span style={visuallyHidden}> {stopName}</span>
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}
